import React, { FC, Suspense } from "react";
import { connect } from "react-redux";
import {
  matchPath,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { createStructuredSelector } from "reselect";
import { useBranding } from "../../branding/BrandingProvider";
import { useSmsNumbersEnabled } from "../../branding/useSmsNumbers";
import LoadingPage from "../../components/LoadingPage/LoadingPage";
import useProgressDelivery from "../../hooks/useProgressDelivery";
import {
  authSelectAllowExtendedTools,
  authSelectAuthenticated,
  authSelectCanUseContainers,
  authSelectFfid,
} from "../../reduxFolder/selectors/globalSelectors";
import { lazyWithRetry } from "../../utils/lazyWithRetry";
import App from "../App";
import BooksAddFunds from "../Books/BooksAddFunds";
import BooksPassports from "../Books/BooksPassports";
import BooksRecipients from "../Books/BooksRecipients";
import BooksTransactionsHistory from "../Books/BooksTransactionsHistory";
import Containers from "../Containers/Containers";
import { deliveryRoutes } from "../Delivery/deliveryRoutes";
import Expected from "../Expected/Expected";
import History from "../History/History";
import Inventory from "../Inventory/Inventory";
import type { LegalDocumentProps } from "../Legal/LegalDocument";
import Outgoing from "../Outgoing/Outgoing";
import Storage from "../Storage/Storage";
import StorageBranches from "../Storage/StorageBranches";
import { getBackgroundLocation } from "./backgroundLocation";
import AuthorizedRoute from "./components/AuthorizedRoute";
import ConditionalRoute from "./components/ConditionalRoute";
import LandingGate from "./components/LandingGate";
import PublicRoute from "./components/PublicRoute";

const SettingsDialog = lazyWithRetry(() => import("../Account/SettingsDialog"));
const SignIn = lazyWithRetry(() => import("../SignIn/SignIn"));
const PasswordReset = lazyWithRetry(
  () => import("../PasswordReset/PasswordReset"),
);
const PasswordChange = lazyWithRetry(
  () => import("../PasswordChange/PasswordChange"),
);
const Parcels = lazyWithRetry(() => import("../Parcels/Parcels"));
const LogOut = lazyWithRetry(() => import("../LogOut/LogOut"));
const Page404 = lazyWithRetry(() => import("../Page404/Page404"));
const SignUp = lazyWithRetry(() => import("../SignUp/SignUp"));
const Delivery = lazyWithRetry(() => import("../Delivery/Delivery"));
const Home = lazyWithRetry(() => import("../Home/Home"));
const HomeArticle = lazyWithRetry(() => import("../Home/HomeArticle"));
const HomeLayout = lazyWithRetry(() => import("../Home/HomeLayout"));
const Impersonate = lazyWithRetry(() => import("../Impersonate/Impersonate"));
const LegalDocument = lazyWithRetry<LegalDocumentProps>(
  () => import("../Legal/LegalDocument"),
);
const Rates = lazyWithRetry(() => import("../Rates/Rates"));
const Books = lazyWithRetry(() => import("../Books/Books"));
const Mailbox = lazyWithRetry(() => import("../Mailbox/Mailbox"));
const Support = lazyWithRetry(() => import("../Support/Support"));
const Tools = lazyWithRetry(() => import("../Tools/Tools"));
const ExtendedTools = lazyWithRetry(() => import("../Tools/ExtendedTools"));
const PrintUsers = lazyWithRetry(() => import("../Tools/PrintUsers"));
const Shopify = lazyWithRetry(() => import("../Shopify/Shopify"));
const Inbound = lazyWithRetry(() => import("../Inbound/Inbound"));
const InboundShipments = lazyWithRetry(
  () => import("../Inbound/InboundShipments"),
);
const InboundAddresses = lazyWithRetry(
  () => import("../Inbound/InboundAddresses"),
);
const ShopifyItems = lazyWithRetry(() => import("../Shopify/ShopifyItems"));
const ShopifyProfiles = lazyWithRetry(
  () => import("../Shopify/ShopifyProfiles"),
);
const Utilities = lazyWithRetry(() => import("../Utilities/Utilities"));
const SmsNumbers = lazyWithRetry(() => import("../Utilities/SmsNumbers"));

interface AppRoutesProps {
  allowExtendedTools: boolean;
  userFfid: string;
  authenticated: boolean | null;
  canUseContainers: boolean;
}

const AppRoutes: FC<AppRoutesProps> = React.memo(
  ({
    allowExtendedTools,
    userFfid,
    authenticated,
    canUseContainers,
    ...rest
  }) => {
    const [access, setAccess] = useProgressDelivery(userFfid);
    const { publicRoot } = useBranding();
    const smsNumbers = useSmsNumbersEnabled();
    const location = useLocation();

    // /account is the settings dialog. It renders beside the page tree, over
    // the page that opened it (the nav-footer link stores it in
    // state.backgroundLocation) or over Home on a cold load. Gated on
    // authenticated === true so an anonymous or still-booting /account stays
    // on the real path and reaches AuthorizedRoute's /signin redirect or
    // LoadingPage through the placeholder route below (LandingGate must not
    // see "/" for an anonymous /account either).
    const settingsOpen =
      authenticated === true &&
      matchPath("/account", location.pathname) !== null;
    // Always pass a location: toggling the prop toggles react-router's scoped
    // LocationContext and would remount App, Layout and every page.
    const pageLocation = settingsOpen
      ? getBackgroundLocation(location) ?? "/"
      : location;

    return (
      <>
        <Routes location={pageLocation}>
          <Route path="/" element={<App />}>
            <Route element={<PublicRoute />}>
              <Route path="signin" element={<SignIn />} />
              <Route path="signup" element={<SignUp />} />
              <Route path="password-reset" element={<PasswordReset />} />
              <Route path="password-change" element={<PasswordChange />} />
              <Route path="logout" element={<LogOut />} />
              <Route path="impersonate" element={<Impersonate />} />
              <Route path="terms" element={<LegalDocument doc="terms" />} />
              {/* Legal pages linked from the marketing landing — partners on the
                signin root never register them, so they 404 there. */}
              {publicRoot === "landing" && (
                <>
                  <Route
                    path="privacy"
                    element={<LegalDocument doc="privacy" />}
                  />
                  <Route
                    path="refund-policy"
                    element={<LegalDocument doc="refund" />}
                  />
                  <Route
                    path="prohibited-items"
                    element={<LegalDocument doc="prohibited" />}
                  />
                </>
              )}
            </Route>

            <Route element={<LandingGate />}>
              <Route element={<AuthorizedRoute />}>
                <Route element={<HomeLayout />}>
                  <Route element={<Home />}>
                    <Route index element={null} />
                    <Route
                      path="home/:sectionCode/:articleCode"
                      element={<HomeArticle />}
                    />
                  </Route>
                </Route>
                <Route path="account" element={null} />
                <Route path="parcels" element={<Parcels />}>
                  <Route index element={<StorageBranches />} />
                  <Route path="inventory/:id" element={<Inventory />} />
                  <Route path="expected" element={<Expected />} />
                  <Route
                    path="outgoing/:id?/:payment_result?"
                    element={<Outgoing />}
                  />
                  <Route path="history" element={<History />} />
                  {/* Hiding the nav links isn't enough — the URL is still
                    typeable, so gate the route itself on the same selector. Safe
                    to read here: AuthorizedRoute holds rendering until the boot
                    getProfile settles, so user_type is loaded by now. */}
                  <Route
                    element={
                      <ConditionalRoute
                        condition={canUseContainers}
                        redirect="/parcels"
                      />
                    }
                  >
                    <Route path="containers" element={<Containers />} />
                  </Route>
                  <Route path=":branchId" element={<Storage />} />
                </Route>
                <Route path="inbound" element={<Inbound />}>
                  <Route index element={<InboundShipments />} />
                  <Route path="addresses" element={<InboundAddresses />} />
                </Route>
                <Route path="mailbox" element={<Mailbox />} />
                <Route path="support" element={<Support />} />
                <Route path="books" element={<Books />}>
                  <Route index element={<BooksTransactionsHistory />} />
                  <Route
                    path="add-funds/:payment_result?"
                    element={<BooksAddFunds />}
                  />
                  <Route path="recipients" element={<BooksRecipients />} />
                  <Route path="passports" element={<BooksPassports />} />
                </Route>
                {/* Partner + USD gate (useSmsNumbersEnabled, shared with the nav
                  item): partners without the feature get a 404 here, like the
                  landing-only legal pages above. */}
                {smsNumbers && (
                  <Route path="utilities" element={<Utilities />}>
                    <Route index element={<Navigate to="sms" replace />} />
                    <Route path="sms" element={<SmsNumbers />} />
                  </Route>
                )}
              </Route>
            </Route>

            <Route
              element={<AuthorizedRoute withMenu={false} redirect="/parcels" />}
            >
              <Route path="shipping" element={<Delivery />}>
                {deliveryRoutes.map(
                  ({ DeliveryComponent, path, redirect, key }) => (
                    <Route
                      key={path}
                      element={
                        <ConditionalRoute
                          condition={key ? access.get("delivery") : true}
                          redirect={redirect}
                        />
                      }
                    >
                      <Route
                        path={path}
                        element={
                          <DeliveryComponent
                            setAccess={setAccess}
                            //@ts-ignore
                            access={access}
                          />
                        }
                      />
                    </Route>
                  ),
                )}
              </Route>
            </Route>

            <Route
              element={
                <AuthorizedRoute
                  condition={authenticated !== true || !allowExtendedTools}
                />
              }
            >
              <Route path="tools" element={<Tools />}>
                <Route index element={<ExtendedTools />} />
                <Route path="print-users" element={<PrintUsers />} />
              </Route>
            </Route>

            <Route
              element={
                <AuthorizedRoute
                  condition={authenticated !== true || !allowExtendedTools}
                />
              }
            >
              <Route path="shopify" element={<Shopify />}>
                <Route index element={<ShopifyItems />} />
                <Route path="profiles" element={<ShopifyProfiles />} />
              </Route>
            </Route>

            {/* Standalone public document — renders its own header/footer, so it
              skips PublicRoute's logo + form chrome and brings its own
              Suspense boundary. */}
            <Route
              path="rates"
              element={
                <Suspense fallback={<LoadingPage />}>
                  <Rates />
                </Suspense>
              }
            />

            <Route path="*" element={<Page404 />} />
          </Route>
        </Routes>
        {/* Outside Layout, so it brings its own Suspense: no LoadingPage swap
          while the chunk loads (the Home article recipe). */}
        {settingsOpen && (
          <Suspense fallback={null}>
            <SettingsDialog />
          </Suspense>
        )}
      </>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    allowExtendedTools: authSelectAllowExtendedTools,
    userFfid: authSelectFfid,
    authenticated: authSelectAuthenticated,
    canUseContainers: authSelectCanUseContainers,
  }),
);

export default withConnect(AppRoutes);
